// Add helper functions at top
let brotliLibPromise = null;

async function ensureBrotliLib() {
  if (brotliLibPromise) return brotliLibPromise;
  
  // Check native support
  if (typeof DecompressionStream !== 'undefined') {
    try {
      new DecompressionStream('br');
      brotliLibPromise = Promise.resolve({ native: true });
      return brotliLibPromise;
    } catch (e) {
      // Native not supported
    }
  }
  
  // Load JS lib
  brotliLibPromise = new Promise((resolve, reject) => {
    if (typeof BrotliDecode !== 'undefined') {
      resolve({ lib: BrotliDecode });
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/brotli/1.3.3/decode.min.js';
    script.onload = () => {
      if (typeof BrotliDecode !== 'undefined') {
        resolve({ lib: BrotliDecode });
      } else {
        reject(new Error('Brotli lib loaded but BrotliDecode not found'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Brotli lib'));
    document.head.appendChild(script);
  });
  
  return brotliLibPromise;
}

async function decompressBrotli(arrayBuffer) {
  const { native, lib } = await ensureBrotliLib();
  
  if (native) {
    const ds = new DecompressionStream('br');
    const writer = ds.writable.getWriter();
    const uint8 = new Uint8Array(arrayBuffer);
    writer.write(uint8);
    writer.close();

    const chunks = [];
    const reader = ds.readable.getReader();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      chunks.push(value);
    }

    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result.buffer;
  } else {
    const uint8 = new Uint8Array(arrayBuffer);
    const decompressedUint8 = lib(uint8);
    if (!decompressedUint8) throw new Error('BrotliDecode failed');
    return decompressedUint8.buffer;
  }
}

function getMimeType(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  const mimes = {
    'js': 'application/javascript',
    'wasm': 'application/wasm',
    'html': 'text/html',
    'css': 'text/css',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
  };
  return mimes[ext] || 'application/octet-stream';
}

function extractAssetPaths(doc) {
  const assets = new Set();
  doc.querySelectorAll('script[src], link[href], img[src], source[src]').forEach(el => {
    const src = el.getAttribute('src') || el.getAttribute('href');
    if (src && !src.startsWith('http') && !src.startsWith('/')) {
      const assetPath = src.startsWith('./') ? src.slice(2) : src;
      if (assetPath.includes('Build/') || assetPath.includes('TemplateData/') || assetPath.endsWith('.wasm') || assetPath.endsWith('.br') || assetPath.endsWith('.js')) {
        assets.add(assetPath);
      }
    }
  });
  return Array.from(assets);
}

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { trackGamePlay } from '../utils/userEngagementService';
import { supabase } from '../utils/supabase';

const WebGPUGamePlayerV2 = ({ game, onClose }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('initializing');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gameError, setGameError] = useState(null);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  const statusMessages = {
    initializing: 'Initializing WebGPU...',
    loading: 'Loading game assets...',
    ready: 'Game ready!',
    error: 'Failed to load game'
  };

  useEffect(() => {
    if (!game || !['webgpu', 'react', 'html5', 'gamedistribution'].includes(game.game_type?.toLowerCase())) return;

    const loadGame = async () => {
      try {
        setLoadingStatus('initializing');
        setGameError(null);
        setLoadingProgress(0);

        // Check if this is a directory-based game
        // Check multiple indicators for directory-based games
        const hasPlaceholderUrls = 
          game.webgpu_loader_url === 'directory-based-game' ||
          game.webgpu_loader_url === 'directory-based' ||
          game.webgpu_data_url === 'directory-based-game' ||
          game.webgpu_framework_url === 'directory-based-game' ||
          game.webgpu_wasm_url === 'directory-based-game';
          
        const isDirectoryBased = 
          game.game_structure_type === 'directory' || 
          hasPlaceholderUrls ||
          (!game.webgpu_loader_url && game.game_directory_path);
        
        console.log('Game loading debug:', {
          title: game.title,
          game_type: game.game_type,
          game_structure_type: game.game_structure_type,
          game_directory_path: game.game_directory_path,
          gamedistribution_url: game.gamedistribution_url,
          hasPlaceholderUrls,
          isDirectoryBased,
          urls: {
            dataUrl: game.webgpu_data_url,
            frameworkUrl: game.webgpu_framework_url,
            codeUrl: game.webgpu_wasm_url,
            loaderUrl: game.webgpu_loader_url
          }
        });
        
        // Handle GameDistribution games
        if (game.game_type?.toLowerCase() === 'gamedistribution') {
          console.log('Loading GameDistribution game:', game.gamedistribution_url);
          setLoadingStatus('loading');
          
          if (!game.gamedistribution_url) {
            setGameError('GameDistribution URL not configured');
            setLoadingStatus('error');
            return;
          }
          
          try {
            // Build the embedded URL directly from the stored GD URL
            // The gamedistribution_url is already the html5.gamedistribution.com embed URL
            const gdUrl = game.gamedistribution_url.replace(/\/$/, '');
            const referrerUrl = `${window.location.origin}/games/${game.id}`;
            const separator = gdUrl.includes('?') ? '&' : '?';
            const embeddedUrl = `${gdUrl}/${separator}gd_sdk_referrer_url=${encodeURIComponent(referrerUrl)}`;

            console.log('GameDistribution embedded URL:', embeddedUrl);
            
            if (iframeRef.current) {
              iframeRef.current.src = embeddedUrl;
              
              // Set loading status to ready immediately
              setLoadingStatus('ready');
              setLoadingProgress(100);
              trackGamePlay();
              
              // Listen for iframe load events
              iframeRef.current.onload = () => {
                console.log('GameDistribution game iframe loaded successfully');
              };
              
              iframeRef.current.onerror = (error) => {
                console.error('Failed to load GameDistribution game iframe:', error);
                setGameError('Failed to load GameDistribution game');
                setLoadingStatus('error');
              };
            }
          } catch (error) {
            console.error('Error loading GameDistribution game:', error);
            setGameError(`Failed to load GameDistribution game: ${error.message}`);
            setLoadingStatus('error');
          }
          
          return; // Exit early for GameDistribution games
        }
        
        if (isDirectoryBased && game.game_directory_path) {
          console.log('Loading directory-based game from:', game.game_directory_path);
          setLoadingStatus('loading');
          
          // Determine the game subdirectory path
          let gameSubPath = '';
          
          // Check if index.html exists in the manifest to determine the correct path
          // Handle both array format and object format manifests
          let manifestFiles = [];
          if (game.game_manifest) {
            if (Array.isArray(game.game_manifest)) {
              manifestFiles = game.game_manifest;
            } else if (typeof game.game_manifest === 'object') {
              // Convert object format to array (for manifests with numbered keys)
              manifestFiles = Object.values(game.game_manifest).filter(item => item && item.path);
            }
          }
          
          const hasIndexInRoot = manifestFiles.some(file => file.path === 'index.html');
          const indexFileEntry = manifestFiles.find(file => file.path && file.path.endsWith('/index.html')) || 
                                 manifestFiles.find(file => file.path === 'index.html');
          
          if (hasIndexInRoot) {
            // Game has index.html in root directory
            gameSubPath = 'index.html';
            console.log('Found index.html in root directory, using path:', gameSubPath);
          } else if (indexFileEntry) {
            // Found index.html in a subdirectory
            gameSubPath = indexFileEntry.path;
            console.log('Found index.html in subdirectory, using path:', gameSubPath);
          }
          // Priority 1: Use the stored game_subdirectory field from database (only if manifest didn't find index.html)
          else if (game.game_subdirectory !== undefined && game.game_subdirectory !== null && !hasIndexInRoot && !indexFileEntry) {
            if (game.game_subdirectory === '') {
              // Root directory
              gameSubPath = 'index.html';
            } else {
              // Subdirectory
              gameSubPath = `${game.game_subdirectory}/index.html`;
            }
            console.log('Using database game_subdirectory:', game.game_subdirectory, '-> path:', gameSubPath);
          }
          // Priority 2: Check manifest for entry point (legacy support)
          else if (game.game_manifest && game.game_manifest.entryPoint) {
            gameSubPath = game.game_manifest.entryPoint;
            console.log('Using manifest entry point:', gameSubPath);
          }
          // Priority 3: Check manifest for gameSubdirectory (legacy support)
          else if (game.game_manifest && game.game_manifest.gameSubdirectory !== undefined) {
            if (game.game_manifest.gameSubdirectory === '') {
              gameSubPath = 'index.html';
            } else {
              gameSubPath = `${game.game_manifest.gameSubdirectory}/index.html`;
            }
            console.log('Using manifest subdirectory:', game.game_manifest.gameSubdirectory, '-> path:', gameSubPath);
          }
          // Priority 4: Check legacy manifest gameDirectory
          else if (game.game_manifest && game.game_manifest.gameDirectory) {
            gameSubPath = `${game.game_manifest.gameDirectory}/index.html`;
            console.log('Using legacy manifest gameDirectory:', gameSubPath);
          }
          // Priority 5: Fallback to known game patterns (temporary fix for mismatched directories)
          else {
            const gameTitle = game.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            
            const knownGamePaths = {
              'project-prismatic': 'prismatic-whitelist/index.html',
              'prismatic': 'prismatic-whitelist/index.html',
              'ashline-racing': 'ashline-whitelist/index.html', // Fix: actual directory is ashline-whitelist
              'ashline': 'ashline-whitelist/index.html'
            };
            
            if (knownGamePaths[gameTitle]) {
              gameSubPath = knownGamePaths[gameTitle];
              console.log('Using known game path for', gameTitle, ':', gameSubPath);
            } else {
              // Default to root directory
              gameSubPath = 'index.html';
              console.log('No subdirectory info found, defaulting to root:', gameSubPath);
            }
          }
          
          // Check game type for different loading strategies
          const isReactGame = game.game_type?.toLowerCase() === 'react';
          const isHTML5Game = game.game_type?.toLowerCase() === 'html5';
          
          // Check if direct mode is enabled via URL parameter for A/B testing
          const urlParams = new URLSearchParams(window.location.search);
          const useDirectMode = urlParams.get('directMode') === 'true';
          
          let gameUrl;
          if (useDirectMode) {
            // TEST: Direct Supabase access (bypasses proxy entirely)
            gameUrl = `https://yagdfjnfkgackswjvyub.supabase.co/storage/v1/object/public/game-assets/${game.game_directory_path}/${gameSubPath}`;
            console.log(`🧪 DIRECT MODE TEST - loading from Supabase:`, gameUrl);
          } else {
            // Strip .br extension from URL (virtual path) to prevent Cloudflare from auto-adding Content-Encoding headers
            // The proxy will internally map to actual .br files in Supabase storage
            let virtualPath = gameSubPath;
            const isWebGPU = game.game_type?.toLowerCase() === 'webgpu';

            if (isWebGPU && gameSubPath.includes('/')) {
              // Remove .br extension from URL path
              virtualPath = gameSubPath.replace(/\.br$/, '');
              if (virtualPath !== gameSubPath) {
                console.log(`[Virtual Path] Stripped .br: ${gameSubPath} → ${virtualPath}`);
              }
            }

            // Use the fixed proxy to bypass caching issues
            gameUrl = `/api/game-fixed/${game.game_directory_path}/${virtualPath}`;
            console.log(`${game.game_type} game - loading via fixed proxy:`, gameUrl);
          }
          
          console.log('Game manifest:', game.game_manifest);

          const gameSubdirectory = gameSubPath.includes('/') ? gameSubPath.split('/')[0] : '';
          console.log('Detected subdirectory:', gameSubdirectory);

          const isWebGPU = game.game_type?.toLowerCase() === 'webgpu';
          
          if (iframeRef.current && isDirectoryBased && game.game_directory_path) {
            if (useDirectMode && isWebGPU) {
              console.log('🚀 Direct client-side WebGPU loading with blob assets');
              try {
                // Client-side direct loading for WebGPU
                const baseUrl = `https://yagdfjnfkgackswjvyub.supabase.co/storage/v1/object/public/game-assets/${game.game_directory_path}/${gameSubdirectory}/`;
                
                // Step 1: Fetch index.html using supabase client to bypass CORS/400
                setLoadingStatus('loading');
                setLoadingProgress(10);
                
                const { data: indexData, error: indexError } = await supabase.storage
                  .from('game-assets')
                  .download(`${game.game_directory_path}/${gameSubPath}`);
                
                if (indexError || !indexData) {
                  throw new Error(`Supabase download failed for index.html: ${indexError?.message}`);
                }
                
                const indexBuffer = await indexData.arrayBuffer();
                const indexText = new TextDecoder().decode(indexBuffer);
                
                console.log('✅ Supabase index.html downloaded:', indexBuffer.byteLength, 'bytes');
                
                // Parse HTML to extract assets
                const parser = new DOMParser();
                const doc = parser.parseFromString(indexText, 'text/html');
                const assetPaths = extractAssetPaths(doc);
                
                // Add common Unity paths if not found
                const commonUnityPaths = [
                  'Build/SHOOT.loader.js',
                  'Build/SHOOT.framework.js.br',
                  'Build/SHOOT.wasm.br',
                  'Build/SHOOT.data.br',
                  'TemplateData/fullscreen.png',
                  'TemplateData/backgroundGradient.png',
                  'TemplateData/pris.png',
                  'TemplateData/strat.png',
                  'TemplateData/hex.png',
                  'TemplateData/style.css'
                ];
                assetPaths.push(...commonUnityPaths.filter(p => !assetPaths.includes(p)));
                
                console.log(`Found ${assetPaths.length} assets to preload:`, assetPaths);
                
                setLoadingProgress(20);
                
                // Step 2: Preload critical Unity assets (raw blobs - Unity handles decompress)
                const criticalAssets = [
                  'Build/SHOOT.loader.js',
                  'Build/SHOOT.framework.js.br',
                  'Build/SHOOT.wasm.br',
                  'Build/SHOOT.data.br'
                ].map(p => `${gameSubdirectory ? gameSubdirectory + '/' : ''}${p}`);
                
                const blobMap = new Map();
                const totalAssets = criticalAssets.length;
                let loadedAssets = 0;
                
                for (const fullPath of criticalAssets) {
                  try {
                    const { data: assetData, error: assetError } = await supabase.storage
                      .from('game-assets')
                      .download(`${game.game_directory_path}/${fullPath}`);
                    
                    if (assetError || !assetData) {
                      console.warn(`Supabase download failed for ${fullPath}: ${assetError?.message}`);
                      continue;
                    }
                    
                    const assetBuffer = await assetData.arrayBuffer();
                    const assetPath = fullPath.split('/').pop();
                    const mime = getMimeType(assetPath);
                    const blob = new Blob([assetBuffer], { type: mime });
                    const blobUrl = URL.createObjectURL(blob);
                    blobMap.set(assetPath, blobUrl);
                    
                    loadedAssets++;
                    const progress = 20 + Math.round((loadedAssets / totalAssets) * 60);
                    setLoadingProgress(Math.min(progress, 85));
                    
                    console.log(`✅ Loaded critical ${assetPath}: ${assetBuffer.byteLength} bytes (raw)`);
                    
                  } catch (err) {
                    console.error(`Error loading critical asset ${fullPath}:`, err);
                  }
                }
                
                console.log('Critical assets loaded:', Array.from(blobMap.keys()));
                
                setLoadingProgress(90);
                
                // Step 3: Rewrite HTML with blob URLs
                let modifiedHtml = indexText;
                blobMap.forEach((blobUrl, path) => {
                  const safePath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                  const regex = new RegExp(`(["'\\/])${safePath}([^\\/"'>]*)?(["'\\/])`, 'g');
                  modifiedHtml = modifiedHtml.replace(regex, `$1${blobUrl}$3`);
                });
                
                // Fix base path for relative assets not caught
                modifiedHtml = modifiedHtml.replace(/<base href="[^"]*"/i, `<base href="${window.location.origin}/">`);
                
                // Remove ServiceWorker registration (blob: doesn't support)
                modifiedHtml = modifiedHtml.replace(/navigator\.serviceWorker\.register\([^)]+\);?/g, '');
                modifiedHtml = modifiedHtml.replace(/if\s*\('serviceWorker' in navigator\)\s*{[^}]*}/g, '');
                
                console.log('Modified HTML ready (SW stripped)');
                
                const htmlBlob = new Blob([modifiedHtml], { type: 'text/html' });
                const finalBlobUrl = URL.createObjectURL(htmlBlob);
                
                // Load iframe
                iframeRef.current.src = finalBlobUrl;
                
                setLoadingProgress(100);
                setLoadingStatus('ready');
                trackGamePlay();
                
                // Store blobs for cleanup
                iframeRef.current._blobUrls = Array.from(blobMap.values()).concat(finalBlobUrl);
                
                console.log('✅ Client-direct WebGPU game loaded with blobs');
                
              } catch (error) {
                console.error('Client-direct load failed:', error);
                setGameError(`Direct mode failed: ${error.message}. Falling back to proxy.`);
                // Fallback to proxy
                const fallbackUrl = `/api/game-fixed/${game.game_directory_path}/${gameSubPath}`;
                iframeRef.current.src = fallbackUrl;
                setLoadingStatus('ready');
              }
            } else {
              // Original proxy logic
              let finalGameUrl = gameUrl;
              if (isReactGame && (game.title?.toLowerCase().includes('solitaire') || game.title?.toLowerCase().includes('unhinged'))) {
                const separator = gameUrl.includes('?') ? '&' : '?';
                finalGameUrl = `${gameUrl}${separator}apiBaseUrl=${encodeURIComponent('https://card-sense-josh638.replit.app')}`;
                console.log('Solitaire game detected - appending custom backend URL:', finalGameUrl);
              }
              
              iframeRef.current.src = finalGameUrl;
              setLoadingStatus('ready');
              setLoadingProgress(100);
              trackGamePlay();
            }
            
            iframeRef.current.onload = () => {
              console.log('Directory-based game iframe loaded successfully');
              
              // Cleanup blobs on load
              if (iframeRef.current._blobUrls) {
                iframeRef.current._blobUrls.forEach(url => URL.revokeObjectURL(url));
                delete iframeRef.current._blobUrls;
              }
              
              // Debug logging...
              setTimeout(() => {
                try {
                  const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                  // ... existing debug code ...
                  let mainElement, containerName;
                  if (game.game_type?.toLowerCase() === 'react') {
                    mainElement = iframeDoc.getElementById('root');
                    containerName = 'root';
                  } else if (game.game_type?.toLowerCase() === 'html5') {
                    mainElement = iframeDoc.getElementById('app') || 
                                 iframeDoc.getElementById('game') || 
                                 iframeDoc.getElementById('main') ||
                                 iframeDoc.querySelector('main') ||
                                 iframeDoc.body.children.length > 0 ? iframeDoc.body : null;
                    containerName = mainElement ? (mainElement.id || mainElement.tagName.toLowerCase()) : 'none';
                  } else {
                    mainElement = iframeDoc.getElementById('root') || iframeDoc.getElementById('app');
                    containerName = mainElement ? mainElement.id : 'none';
                  }
                  
                  const hasReactScripts = Array.from(iframeDoc.scripts).some(script => 
                    script.src && (script.src.includes('react') || script.src.includes('bundle'))
                  );
                  
                  console.log('🔍 Game App Debug:', {
                    hasMainElement: !!mainElement,
                    containerName: containerName,
                    mainElementHasChildren: mainElement ? mainElement.children.length > 0 : false,
                    documentTitle: iframeDoc.title,
                    bodyClasses: iframeDoc.body.className,
                    scriptsLoaded: iframeDoc.scripts.length,
                    stylesLoaded: iframeDoc.styleSheets.length,
                    hasReactScripts,
                    gameType: game.game_type,
                    bodyContent: iframeDoc.body.innerHTML.substring(0, 500) + '...'
                  });
                  
                  const hasReactRoot = game.game_type?.toLowerCase() === 'react' && mainElement && mainElement.children.length > 0;
                  const hasReactDevTools = iframeRef.current.contentWindow.__REACT_DEVTOOLS_GLOBAL_HOOK__;
                  
                  let actualGameType = 'Unknown';
                  if (game.game_type?.toLowerCase() === 'html5' && mainElement) {
                    actualGameType = 'HTML5';
                  } else if (game.game_type?.toLowerCase() === 'react' && (hasReactRoot || hasReactScripts)) {
                    actualGameType = 'React';
                  } else if (mainElement && iframeDoc.body.children.length > 0) {
                    actualGameType = 'HTML5';
                  }
                  
                  console.log('🔍 Game Type Detection:', {
                    hasReactRoot,
                    hasReactDevTools,
                    hasReactScripts,
                    actualGameType,
                    databaseGameType: game.game_type,
                    mainElementContent: mainElement ? mainElement.innerHTML.substring(0, 200) + '...' : 'No main element found'
                  });
                  
                  if (game.game_type?.toLowerCase() === 'html5' && mainElement) {
                    console.log('✅ HTML5 game loaded successfully - found container:', containerName);
                  } else if (game.game_type?.toLowerCase() === 'react' && !hasReactRoot && hasReactScripts) {
                    console.warn('⚠️ React app may not have mounted properly - root element is empty');
                  } else if (!mainElement) {
                    console.warn('⚠️ No main container element found for game');
                  }
                  
                } catch (error) {
                  console.warn('Could not access iframe content for debugging (likely CORS):', error.message);
                }
              }, 3000);
            };
            
            iframeRef.current.onerror = (error) => {
              console.error('Failed to load directory-based game iframe:', error);
              setGameError('Failed to load directory-based game');
              setLoadingStatus('error');
            };
          }
        } else if (isDirectoryBased && !game.game_directory_path) {
          // Directory-based game but missing directory path
          console.error('Directory-based game missing directory path');
          setGameError('Directory-based game configuration incomplete - missing directory path');
          setLoadingStatus('error');
        } else {
          // Legacy loading method - use the original WebGPUGamePlayer logic
          // Import and use the Unity loading logic from WebGPUGamePlayer
          setLoadingStatus('loading');
          
          // Helper function to convert storage URLs to Vercel Edge Function URLs
          const getVercelUrl = (storageUrl) => {
            if (!storageUrl || storageUrl === 'directory-based' || storageUrl === 'directory-based-game') return null;
            
            const match = storageUrl.match(/\/storage\/v1\/object\/public\/game-assets\/(.+)$/);
            if (match) {
              if (match[1].endsWith('.br')) {
                return `/api/webgpu?path=${encodeURIComponent(match[1])}`;
              } else {
                return storageUrl;
              }
            }
            return storageUrl;
          };

          // Check if we have valid URLs
          const loaderUrl = getVercelUrl(game.webgpu_loader_url);
          if (!loaderUrl) {
            setGameError('Invalid game configuration - missing loader URL for legacy game');
            setLoadingStatus('error');
            return;
          }

          // Unity configuration
          const config = {
            arguments: [],
            dataUrl: getVercelUrl(game.webgpu_data_url),
            frameworkUrl: getVercelUrl(game.webgpu_framework_url),
            codeUrl: getVercelUrl(game.webgpu_wasm_url),
            streamingAssetsUrl: game.webgpu_config?.streamingAssetsUrl || "StreamingAssets",
            companyName: game.webgpu_config?.companyName || "DefaultCompany",
            productName: game.webgpu_config?.productName || game.title,
            productVersion: game.webgpu_config?.productVersion || "1.0",
            autoSyncPersistentDataPath: true, // Enable automatic sync to avoid deprecation warning
            showBanner: (msg, type) => {
              console.log(`Unity Banner [${type}]: ${msg}`);
              if (type === 'error') {
                setGameError(msg);
                setLoadingStatus('error');
              }
            },
            matchWebGLToCanvasSize: true,
          };

          // Load Unity loader script
          const script = document.createElement("script");
          script.src = loaderUrl;
          
          script.onload = () => {
            console.log('Unity loader script loaded');
            if (window.createUnityInstance && iframeRef.current) {
              // Create a canvas element for Unity
              const canvas = document.createElement('canvas');
              canvas.width = 960;
              canvas.height = 600;
              canvas.style.width = '100%';
              canvas.style.height = '100%';
              canvas.style.display = 'block';
              
              // Clear iframe and add canvas
              iframeRef.current.style.display = 'none';
              iframeRef.current.parentNode.appendChild(canvas);
              
              window.createUnityInstance(canvas, config, (progress) => {
                const progressPercent = Math.round(progress * 100);
                setLoadingProgress(progressPercent);
              }).then((instance) => {
                console.log('Unity instance created successfully');
                setLoadingStatus('ready');
                setLoadingProgress(100);
                trackGamePlay();
              }).catch((message) => {
                console.error('Unity loading error:', message);
                setGameError(message || 'Failed to create Unity instance');
                setLoadingStatus('error');
              });
            } else {
              setGameError('Unity loader not available');
              setLoadingStatus('error');
            }
          };
          
          script.onerror = (error) => {
            console.error('Failed to load Unity loader script:', error);
            setGameError(`Failed to load Unity loader script`);
            setLoadingStatus('error');
          };
          
          document.body.appendChild(script);
        }
      } catch (error) {
        console.error('Error loading game:', error);
        setGameError(error.message);
        setLoadingStatus('error');
      }
    };

    loadGame();
  }, [game]);

  const handleFullscreen = () => {
    if (!containerRef.current) return;

    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        containerRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  const handleClose = () => {
    // Cleanup blobs
    if (iframeRef.current?._blobUrls) {
      iframeRef.current._blobUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      delete iframeRef.current._blobUrls;
    }

    // Reset state
    setLoadingProgress(0);
    setLoadingStatus('initializing');
    setGameError(null);
    setIsFullscreen(false);

    onClose();
  };

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle IAP purchase messages from game iframe
  useEffect(() => {
    const handleIAPMessage = async (event) => {
      // Only handle IAP purchase messages
      if (event.data.type !== 'SOLIS_IAP_PURCHASE') {
        return;
      }

      const { productId, gameId, userId } = event.data.data;

      try {
        // Call create-iap-purchase Edge Function
        const { data, error } = await supabase.functions.invoke('create-iap-purchase', {
          body: {
            productId,
            gameId: gameId || game.id,
            userId,
          },
        });

        if (error) throw error;

        if (data.success && data.clientSecret) {
          // Load Stripe.js if not already loaded
          if (!window.Stripe) {
            const script = document.createElement('script');
            script.src = 'https://js.stripe.com/v3/';
            script.async = true;
            document.head.appendChild(script);
            await new Promise((resolve) => {
              script.onload = resolve;
            });
          }

          // Initialize Stripe
          const stripe = window.Stripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

          // Show Stripe payment modal
          const { error: stripeError } = await stripe.confirmPayment({
            clientSecret: data.clientSecret,
            confirmParams: {
              return_url: window.location.href, // Stay on same page
            },
            redirect: 'if_required', // Don't redirect if not needed
          });

          if (stripeError) {
            // Payment failed
            if (iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage({
                type: 'SOLIS_IAP_RESULT',
                success: false,
                error: stripeError.message,
              }, '*');
            }
          } else {
            // Payment succeeded
            if (iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage({
                type: 'SOLIS_IAP_RESULT',
                success: true,
                purchase: {
                  id: data.purchaseId,
                  productId: productId,
                  amount: data.amount,
                  currency: data.currency,
                },
              }, '*');
            }
          }
        } else {
          throw new Error(data.error || 'Failed to create purchase');
        }
      } catch (error) {
        console.error('IAP purchase error:', error);

        // Send error back to game
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'SOLIS_IAP_RESULT',
            success: false,
            error: error.message || 'Purchase failed',
          }, '*');
        }
      }
    };

    window.addEventListener('message', handleIAPMessage);

    return () => {
      window.removeEventListener('message', handleIAPMessage);
    };
  }, [game?.id]);

  if (!game || !['webgpu', 'react', 'html5', 'gamedistribution'].includes(game.game_type?.toLowerCase())) return null;

  return (
    <div className="w-full h-full bg-black flex flex-col">
      <div ref={containerRef} className="flex-1 flex flex-col">
        {/* Game Controls Header */}
        <div className="bg-background/90 backdrop-blur-sm border-b border-border p-4 flex items-center justify-between relative z-10">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              {game.cover_image_url && (
                <img 
                  src={game.cover_image_url} 
                  alt={game.title}
                  className="w-8 h-8 rounded object-cover"
                />
              )}
              <div>
                <h3 className="font-semibold text-foreground">{game.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {statusMessages[loadingStatus]}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {loadingStatus === 'ready' && (
              <button
                onClick={handleFullscreen}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent hover:bg-muted text-foreground hover:text-primary transition-colors cursor-pointer"
                style={{ 
                  WebkitAppearance: 'none',
                  border: 'none',
                  outline: 'none',
                  userSelect: 'none',
                  touchAction: 'manipulation'
                }}
                type="button"
              >
                <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={16} />
              </button>
            )}
            <button
              onClick={handleClose}
              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent hover:bg-muted text-foreground hover:text-destructive transition-colors cursor-pointer"
              style={{ 
                WebkitAppearance: 'none',
                border: 'none',
                outline: 'none',
                userSelect: 'none',
                touchAction: 'manipulation'
              }}
              type="button"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Game Container */}
        <div className="flex-1 flex items-center justify-center bg-black relative">
          {game.game_type?.toLowerCase() === 'gamedistribution' ? (
            // GameDistribution games - no sandbox needed (cross-origin already isolates them)
            // Sandbox breaks GD's ad SDK causing "adsBlocked" false positive
            <iframe
              ref={iframeRef}
              className="w-full h-full border-0"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'transparent'
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; gamepad; midi; xr-spatial-tracking; web-share; storage-access"
              allowFullScreen
            />
          ) : (game.game_structure_type === 'directory' || 
            game.webgpu_loader_url === 'directory-based-game' ||
            game.webgpu_loader_url === 'directory-based') ? (
            // Directory-based game - use proper sandbox permissions based on game type
            (() => {
              const gameType = game.game_type?.toLowerCase();
              console.log('🔍 Iframe sandbox selection for game type:', gameType);
              
              if (gameType === 'react') {
                console.log('✅ Using React iframe sandbox');
                return (
                  // React games - permissive sandbox for full React functionality
                  <iframe
                    ref={iframeRef}
                    className="w-full h-full border-0"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent'
                    }}
                    sandbox="allow-scripts allow-forms allow-popups allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-downloads allow-storage-access-by-user-activation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; gamepad; midi; xr-spatial-tracking; web-share; storage-access"
                    allowFullScreen
                  />
                );
              } else if (gameType === 'html5') {
                console.log('✅ Using HTML5 iframe sandbox');
                return (
                  // HTML5 games - permissive sandbox for full HTML5 functionality
                  <iframe
                    ref={iframeRef}
                    className="w-full h-full border-0"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent'
                    }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-downloads allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; gamepad; midi; xr-spatial-tracking; web-share; storage-access"
                    allowFullScreen
                  />
                );
              } else {
                console.log('✅ Using Unity iframe sandbox');
                return (
                  // Unity games - with sandbox restrictions
                  <iframe
                    ref={iframeRef}
                    className="w-full h-full border-0"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      background: 'transparent'
                    }}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-downloads allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; gamepad; midi; xr-spatial-tracking; web-share; storage-access"
                    allowFullScreen
                  />
                );
              }
            })()
          ) : (
            // Legacy game - show error for now
            <div className="text-center text-white">
              <p>Legacy game loading not implemented</p>
            </div>
          )}

          {/* Loading Overlay */}
          {loadingStatus !== 'ready' && !gameError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/90 via-black/80 to-black/90">
              <div className="text-center max-w-md mx-4">
                {/* Game Cover Image */}
                {game.cover_image_url && (
                  <div className="w-24 h-24 mx-auto mb-6 rounded-xl overflow-hidden shadow-2xl ring-2 ring-white/20">
                    <img 
                      src={game.cover_image_url} 
                      alt={game.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Game Title */}
                <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">
                  {game.title}
                </h3>
                
                {/* Loading Status */}
                <p className="text-white/70 mb-8 text-lg">
                  {statusMessages[loadingStatus]}
                </p>

                {/* Loading Spinner */}
                {loadingStatus === 'loading' && (
                  <div className="mb-6">
                    <div className="relative w-20 h-20 mx-auto">
                      <div className="absolute inset-0 border-4 border-primary/30 rounded-full animate-ping" />
                      <div className="absolute inset-2 border-4 border-primary/50 rounded-full animate-ping animation-delay-200" />
                      <div className="absolute inset-4 border-4 border-primary rounded-full animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Overlay */}
          {gameError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/90">
              <div className="text-center max-w-md mx-4">
                <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  Failed to Load Game
                </h3>
                <p className="text-white/70 mb-6">
                  {gameError}
                </p>
                <div className="space-y-3">
                  <Button
                    variant="default"
                    onClick={() => window.location.reload()}
                    iconName="RotateCcw"
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    className="ml-3"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Game Info Footer (only show when not in fullscreen) */}
        {!isFullscreen && loadingStatus === 'ready' && (
          <div className="bg-background/90 backdrop-blur-sm border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-muted-foreground">
                  Playing: <span className="text-foreground font-medium">{game.title}</span>
                </div>
                {game.estimated_play_time && (
                  <div className="text-sm text-muted-foreground">
                    Est. {game.estimated_play_time} min
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                  <Icon name="Users" size={14} />
                  <span>{game.play_count || 0} plays</span>
                </div>
                {game.average_rating > 0 && (
                  <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                    <Icon name="Star" size={14} className="text-yellow-500" />
                    <span>{game.average_rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WebGPUGamePlayerV2;
